import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import SeoHead from '@/components/feature/SeoHead';
import PageHero from '@/components/feature/PageHero';
import ScheduleRow from '@/components/feature/ScheduleRow';
import SubscribeForm from '@/components/feature/SubscribeForm';
import { liveSessions } from '@/mocks/lives';
import { categories } from '@/mocks/site';
import { formatCount, todayLabel, todayWeekday } from '@/utils/time';

const statusFilters = [
  { key: 'all', label: '全部状态' },
  { key: 'live', label: '正在直播' },
  { key: 'upcoming', label: '即将开始' },
  { key: 'ended', label: '已结束' },
];

const categoryOptions = ['全部', ...categories.map((item) => item.key)];

export default function SchedulePage() {
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') ?? '全部');
  const [activeStatus, setActiveStatus] = useState('all');
  const [keyword, setKeyword] = useState(searchParams.get('keyword') ?? '');
  const [draft, setDraft] = useState(searchParams.get('keyword') ?? '');

  useEffect(() => {
    if (window.location.hash === '#subscribe') {
      const el = document.getElementById('subscribe');
      if (el) window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }, []);

  const sorted = useMemo(
    () => [...liveSessions].sort((a, b) => a.start.localeCompare(b.start)),
    [],
  );

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return sorted.filter((session) => {
      const matchCategory = activeCategory === '全部' || session.category === activeCategory;
      const matchStatus = activeStatus === 'all' || session.status === activeStatus;
      const matchKeyword =
        !kw ||
        session.title.toLowerCase().includes(kw) ||
        session.host.toLowerCase().includes(kw) ||
        session.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchStatus && matchKeyword;
    });
  }, [sorted, activeCategory, activeStatus, keyword]);

  const upcoming = sorted.filter((session) => session.status === 'upcoming').slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://3yp0uh.cn/' },
          { '@type': 'ListItem', position: 2, name: '今日直播排期', item: 'https://3yp0uh.cn/schedule' },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: '今日直播排期',
        url: 'https://3yp0uh.cn/schedule',
        inLanguage: 'zh-CN',
        description: '按开播时间排序的今日直播排期，包含正在直播、即将开始场次与已结束场次。',
      },
      {
        '@type': 'ItemList',
        name: '今日直播排期列表',
        numberOfItems: liveSessions.length,
        itemListElement: sorted.map((session, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: session.title,
          url: `https://3yp0uh.cn/live/${session.id}`,
        })),
      },
    ],
  };

  return (
    <SiteLayout>
      <SeoHead
        title="今日直播排期 - 爆料直播网全场次时间表"
        description="爆料直播网今日直播排期页，按开播时间整理全天直播场次，支持按栏目和状态筛选、关键词搜索，正在直播与即将开始场次一眼看清。"
        keywords="今日直播排期,爆料直播,即将开始场次,直播场次时间表,正在直播"
        path="/schedule"
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow={`${todayLabel()} ${todayWeekday()} · 共 ${liveSessions.length} 场`}
        title="今日直播排期"
        desc="全天场次按开播时间顺序排列，可结合栏目与状态快速筛选，也可以直接搜索主播或关键词，找到想看的场次并提前设提醒。"
        crumbs={[{ label: '首页', path: '/' }, { label: '今日排期' }]}
      >
        <div className="flex flex-wrap gap-3">
          <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
            <p className="text-xs text-foreground-500">正在直播</p>
            <p className="mt-1 font-heading text-lg font-bold text-accent-600">
              {liveSessions.filter((item) => item.status === 'live').length} 场
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
            <p className="text-xs text-foreground-500">即将开始</p>
            <p className="mt-1 font-heading text-lg font-bold text-primary-700">
              {liveSessions.filter((item) => item.status === 'upcoming').length} 场
            </p>
          </div>
        </div>
      </PageHero>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="container-page grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-12">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  setKeyword(draft);
                }}
                className="flex flex-col gap-3 sm:flex-row"
              >
                <label className="flex flex-1 items-center gap-2 rounded-md border border-background-300 px-3 py-2.5 focus-within:border-primary-400">
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                    <i className="ri-search-line" aria-hidden="true" />
                  </span>
                  <input
                    type="search"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder="搜索场次标题、主播或标签"
                    className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  />
                </label>
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  搜索场次
                </button>
              </form>

              <div className="mt-4 flex flex-col gap-3">
                <div className="flex flex-wrap gap-2">
                  {categoryOptions.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setActiveCategory(item)}
                      className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                        activeCategory === item
                          ? 'border-secondary-500 bg-secondary-500 font-medium text-background-50'
                          : 'border-background-300 bg-background-50 text-foreground-600 hover:border-secondary-400 hover:text-secondary-700'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 border-t border-background-200 pt-3">
                  {statusFilters.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setActiveStatus(item.key)}
                      className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                        activeStatus === item.key
                          ? 'border-primary-500 bg-primary-500 font-medium text-background-50'
                          : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-5 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
              共找到
              <strong className="font-semibold text-foreground-950">{filtered.length}</strong>
              场直播
              {keyword && (
                <span className="text-foreground-500">
                  （关键词：{keyword}
                  <button
                    type="button"
                    onClick={() => {
                      setKeyword('');
                      setDraft('');
                    }}
                    className="ml-1.5 text-primary-700 hover:text-primary-800"
                  >
                    清除
                  </button>
                  ）
                </span>
              )}
            </p>

            {filtered.length > 0 ? (
              <ul className="mt-4">
                {filtered.map((session) => (
                  <ScheduleRow key={session.id} session={session} />
                ))}
              </ul>
            ) : (
              <div className="mt-4 rounded-lg border border-dashed border-background-300 bg-background-100 px-5 py-14 text-center">
                <p className="text-sm text-foreground-600">没有符合条件的场次。</p>
                <p className="mt-2 text-xs text-foreground-500">
                  试着换个栏目或清空关键词，也可以订阅提醒接收后续排期。
                </p>
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">即将开始场次</h2>
              <ul className="mt-4 flex flex-col gap-4">
                {upcoming.map((session) => (
                  <li key={session.id} className="border-b border-background-200 pb-4 last:border-0 last:pb-0">
                    <p className="flex items-center gap-2 text-xs text-foreground-500">
                      <span className="rounded bg-primary-100 px-2 py-0.5 font-medium text-primary-800">
                        {session.start}
                      </span>
                      {session.category}
                    </p>
                    <p className="mt-2 font-heading text-sm font-semibold leading-snug text-foreground-950">
                      {session.title}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">
                      主播 {session.host} · 热度 {formatCount(session.hot)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">排期说明</h2>
              <ul className="mt-3 flex flex-col gap-3 text-xs leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                    <i className="ri-time-line" aria-hidden="true" />
                  </span>
                  页面时间按北京时间（UTC+8）展示，每 15 分钟校对一次。
                </li>
                <li className="flex gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                    <i className="ri-shuffle-line" aria-hidden="true" />
                  </span>
                  临时改档或加场会即时刷新，最终以主播开播通知为准。
                </li>
                <li className="flex gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                    <i className="ri-history-line" aria-hidden="true" />
                  </span>
                  已结束场次可在回放列表补看，常规保留 30 天。
                </li>
              </ul>
              <p className="mt-4 text-xs text-foreground-500">
                最近校验：{todayLabel()} {todayWeekday()}
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section id="subscribe" className="w-full bg-primary-950 py-12 md:py-16">
        <div className="container-page grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-800 px-3 py-1 text-xs font-medium text-primary-200">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-notification-badge-line" aria-hidden="true" />
              </span>
              排期提醒
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-[32px]">
              把想看的场次交给提醒
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-background-300">
              订阅后，当天重点场次开始前 30 分钟会收到一封提醒邮件，包含场次名称、主播与看点摘要，
              这样就不用一直盯着排期页刷新了。
            </p>
          </div>
          <SubscribeForm />
        </div>
      </section>

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="container-page">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">按栏目查看排期</h2>
          <p className="mt-2 text-sm text-foreground-600">
            除了完整时间轴，也可以直接进入感兴趣的栏目，查看该方向当天与往期的场次安排。
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  setActiveCategory(item.key);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={item.icon} aria-hidden="true" />
                </span>
                {item.key}
                <span className="text-foreground-400">
                  {liveSessions.filter((session) => session.category === item.key).length}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-6 text-xs text-foreground-500">
            提示：即将开始场次倒计时会随开播时间自动更新，若场次已开始，卡片会切换为「直播进行中」状态。
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}